import type { AxiosInstance } from 'axios';

type AuthShape = { accessToken?: string | null };

const withAuth = (auth: AuthShape) => ({
  headers: { Authorization: 'Bearer ' + String(auth?.accessToken || '') },
});

const toArray = (value: any): any[] => (Array.isArray(value) ? value : []);

export const parseSuppliersResponse = (response: any) => {
  const res = response?.data;
  if (!res) return null;

  if (res.success === true && Array.isArray(res.suppliers)) {
    return {
      success: true,
      suppliers: res.suppliers,
      total: Number(res.total ?? res.suppliers.length),
      page: Number(res.page ?? 1),
      limit: Number(res.limit ?? 50),
      message: String(res.message || ''),
    };
  }

  if (res.status === 200 && res.data) {
    const d = res.data;
    const suppliers = Array.isArray(d.suppliers)
      ? d.suppliers
      : toArray(d.docs ?? d.data);
    return {
      success: true,
      suppliers,
      total: Number(d.total ?? d.totalDocs ?? suppliers.length),
      page: Number(d.page ?? d.currentPage ?? 1),
      limit: Number(d.limit ?? d.perPage ?? 50),
      message: String(res.message || ''),
    };
  }

  return null;
};

export const parseSupplierResponse = (response: any) => {
  const res = response?.data;
  if (!res) return null;

  if (res.success === true && res.supplier) {
    return {
      success: true,
      supplier: res.supplier,
      message: String(res.message || ''),
    };
  }

  if (res.status === 200 && res.data) {
    return {
      success: true,
      supplier: res.data.supplier ?? res.data,
      message: String(res.message || ''),
    };
  }

  return null;
};

export const parsePurchaseOrdersResponse = (response: any) => {
  const res = response?.data;
  if (!res) return null;

  if (res.success === true) {
    const purchaseOrders = toArray(
      res.purchaseOrders ?? res.purchase_orders ?? res.data,
    );
    return {
      success: true,
      purchaseOrders,
      total: Number(res.total ?? purchaseOrders.length),
      message: String(res.message || ''),
    };
  }

  if (res.status === 200 && res.data) {
    const d = res.data;
    const purchaseOrders = Array.isArray(d.purchaseOrders)
      ? d.purchaseOrders
      : toArray(d.docs ?? d.data);
    return {
      success: true,
      purchaseOrders,
      total: Number(d.total ?? d.totalDocs ?? purchaseOrders.length),
      message: String(res.message || ''),
    };
  }

  return null;
};

export const parsePurchaseOrderResponse = (response: any) => {
  const res = response?.data;
  if (!res) return null;

  if (res.success === true) {
    const purchaseOrder =
      res.purchaseOrder ??
      res?.data?.purchaseOrder ??
      (res?.data &&
      !Array.isArray(res.data) &&
      (res.data.bp_purchase_order_number != null ||
        res.data.bp_purchase_order_status != null)
        ? res.data
        : null);
    if (!purchaseOrder) return null;
    return {
      success: true,
      purchaseOrder,
      message: String(res.message || ''),
    };
  }

  if (res.status === 200 && res.data) {
    return {
      success: true,
      purchaseOrder: res.data.purchaseOrder ?? res.data,
      message: String(res.message || ''),
    };
  }

  return null;
};

export const parseCreatedInvoiceResponse = (response: any) => {
  const res = response?.data;
  if (!res) return null;

  if (res.success === true) {
    const invoice =
      res.invoice ??
      res?.data?.invoice ??
      (res?.data &&
      !Array.isArray(res.data) &&
      (res.data.bp_supplier_invoice_number != null ||
        res.data.bp_supplier_invoice_total_amount != null ||
        res.data.bp_supplier_invoice_supplier_id != null ||
        res.data.bp_supplier_invoice_purchase_order_id != null)
        ? res.data
        : null);
    return {
      success: true,
      invoice,
      purchaseOrder: res.purchaseOrder ?? res?.data?.purchaseOrder ?? null,
      message: String(res.message || ''),
    };
  }

  if (res.status === 200 && res.data) {
    const d = res.data;
    const invoice =
      d.invoice ??
      (d.bp_supplier_invoice_number != null ||
      d.bp_supplier_invoice_total_amount != null ||
      d.bp_supplier_invoice_supplier_id != null ||
      d.bp_supplier_invoice_purchase_order_id != null
        ? d
        : null);
    return {
      success: true,
      invoice,
      purchaseOrder: d.purchaseOrder ?? null,
      message: String(res.message || ''),
    };
  }

  return null;
};

export const parseCreatedGoodsReceiptResponse = (response: any) => {
  const res = response?.data;
  if (!res) return null;

  if (res.success === true) {
    const goodsReceipt =
      res.goodsReceipt ??
      res?.data?.goodsReceipt ??
      (res?.data &&
      !Array.isArray(res.data) &&
      (res.data.bp_goods_receipt_number != null ||
        res.data.bp_goods_receipt_date != null ||
        res.data.bp_goods_receipt_notes != null)
        ? res.data
        : null);
    return {
      success: true,
      goodsReceipt,
      purchaseOrder: res.purchaseOrder ?? res?.data?.purchaseOrder ?? null,
      message: String(res.message || ''),
    };
  }

  if (res.status === 200 && res.data) {
    const d = res.data;
    const goodsReceipt =
      d.goodsReceipt ??
      (d.bp_goods_receipt_number != null ||
      d.bp_goods_receipt_date != null ||
      d.bp_goods_receipt_notes != null
        ? d
        : null);
    return {
      success: true,
      goodsReceipt,
      purchaseOrder: d.purchaseOrder ?? null,
      message: String(res.message || ''),
    };
  }

  return null;
};

export const parseInvoicesResponse = (response: any) => {
  const res = response?.data;
  if (!res) return null;

  if (res.success === true) {
    const invoices = toArray(res.invoices ?? res.data);
    return {
      success: true,
      invoices,
      total: Number(res.total ?? invoices.length),
      message: String(res.message || ''),
    };
  }

  if (res.status === 200 && res.data) {
    const d = res.data;
    const invoices = Array.isArray(d.invoices)
      ? d.invoices
      : toArray(d.docs ?? d.data);
    return {
      success: true,
      invoices,
      total: Number(d.total ?? d.totalDocs ?? invoices.length),
      message: String(res.message || ''),
    };
  }

  return null;
};

export const parseGoodsReceiptsResponse = (response: any) => {
  const res = response?.data;
  if (!res) return null;

  if (res.success === true) {
    const goodsReceipts = toArray(
      res.goodsReceipts ?? res.goods_receipts ?? res.data,
    );
    return {
      success: true,
      goodsReceipts,
      total: Number(res.total ?? goodsReceipts.length),
      message: String(res.message || ''),
    };
  }

  if (res.status === 200 && res.data) {
    const d = res.data;
    const goodsReceipts = Array.isArray(d.goodsReceipts)
      ? d.goodsReceipts
      : toArray(d.docs ?? d.data);
    return {
      success: true,
      goodsReceipts,
      total: Number(d.total ?? d.totalDocs ?? goodsReceipts.length),
      message: String(res.message || ''),
    };
  }

  return null;
};

const getSuppliersCore = async (
  axiosinstance: AxiosInstance,
  page: number,
  limit: number,
  status: string | undefined,
  auth: AuthShape,
) => {
  const parts: string[] = [
    `page=${page}`,
    `limit=${limit}`,
  ];
  if (status && status.trim().length > 0) parts.push(`status=${encodeURIComponent(status.trim())}`);
  return axiosinstance.get(
    `/bp_get_suppliers_information?${parts.join('&')}`,
    withAuth(auth),
  );
};

const getSupplierByIdCore = async (
  axiosinstance: AxiosInstance,
  supplierId: string,
  auth: AuthShape,
) => {
  return axiosinstance.get(
    `bp_get_supplier_information/${supplierId}`,
    withAuth(auth),
  );
};

const addSupplierCore = async (
  axiosinstance: AxiosInstance,
  payload: any,
  auth: AuthShape,
) => {
  return axiosinstance.post(
    'bp_add_supplier_information',
    payload,
    withAuth(auth),
  );
};

const updateSupplierCore = async (
  axiosinstance: AxiosInstance,
  supplierId: string,
  payload: any,
  auth: AuthShape,
) => {
  return axiosinstance.patch(
    `bp_update_supplier_information/${supplierId}`,
    payload,
    withAuth(auth),
  );
};

const getPurchaseOrdersCore = async (
  axiosinstance: AxiosInstance,
  options: { supplierId?: string; purchaseOrderId?: string },
  auth: AuthShape,
) => {
  const parts: string[] = [];
  if (options.supplierId) parts.push(`supplierId=${encodeURIComponent(options.supplierId)}`);
  if (options.purchaseOrderId) parts.push(`purchaseOrderId=${encodeURIComponent(options.purchaseOrderId)}`);
  return axiosinstance.get(
    `bp_get_purchase_orders_information?${parts.join('&')}`,
    withAuth(auth),
  );
};

const getPurchaseOrderByIdCore = async (
  axiosinstance: AxiosInstance,
  purchaseOrderId: string,
  auth: AuthShape,
) => {
  return axiosinstance.get(
    `bp_get_purchase_order_information/${purchaseOrderId}`,
    withAuth(auth),
  );
};

const addPurchaseOrderCore = async (
  axiosinstance: AxiosInstance,
  payload: any,
  auth: AuthShape,
) => {
  return axiosinstance.post(
    'bp_add_purchase_order_information',
    payload,
    withAuth(auth),
  );
};

const updatePurchaseOrderCore = async (
  axiosinstance: AxiosInstance,
  purchaseOrderId: string,
  payload: any,
  auth: AuthShape,
) => {
  return axiosinstance.patch(
    `bp_update_purchase_order_information/${purchaseOrderId}`,
    payload,
    withAuth(auth),
  );
};

const sendPurchaseOrderCore = async (
  axiosinstance: AxiosInstance,
  payload: { purchaseOrderId: string; channels: string[] },
  auth: AuthShape,
) => {
  return axiosinstance.post(
    'bp_send_purchase_order_to_supplier',
    payload,
    withAuth(auth),
  );
};

const cancelPurchaseOrderCore = async (
  axiosinstance: AxiosInstance,
  payload: { purchaseOrderId: string },
  auth: AuthShape,
) => {
  return axiosinstance.post(
    'bp_cancel_purchase_order_information',
    payload,
    withAuth(auth),
  );
};

const getSupplierInvoicesCore = async (
  axiosinstance: AxiosInstance,
  options: { supplierId?: string; purchaseOrderId?: string },
  auth: AuthShape,
) => {
  const parts: string[] = [];
  if (options.supplierId) parts.push(`supplierId=${encodeURIComponent(options.supplierId)}`);
  if (options.purchaseOrderId) parts.push(`purchaseOrderId=${encodeURIComponent(options.purchaseOrderId)}`);
  return axiosinstance.get(
    `bp_get_supplier_invoices_information?${parts.join('&')}`,
    withAuth(auth),
  );
};

const addSupplierInvoiceCore = async (
  axiosinstance: AxiosInstance,
  payload: any,
  auth: AuthShape,
) => {
  return axiosinstance.post(
    'bp_add_supplier_invoice_information',
    payload,
    withAuth(auth),
  );
};

const getGoodsReceiptsCore = async (
  axiosinstance: AxiosInstance,
  purchaseOrderId: string,
  auth: AuthShape,
) => {
  return axiosinstance.get(
    `bp_get_goods_receipts_information?purchaseOrderId=${encodeURIComponent(purchaseOrderId)}`,
    withAuth(auth),
  );
};

const getPurchaseOrderForReceiptCore = async (
  axiosinstance: AxiosInstance,
  purchaseOrderId: string,
  auth: AuthShape,
) => {
  return axiosinstance.get(
    `bp_get_purchase_order_for_receipt/${purchaseOrderId}`,
    withAuth(auth),
  );
};

const createGoodsReceiptCore = async (
  axiosinstance: AxiosInstance,
  payload: any,
  auth: AuthShape,
) => {
  return axiosinstance.post('bp_create_goods_receipt', payload, withAuth(auth));
};

export const getSuppliersInformation = async (
  axiosinstance: AxiosInstance,
  page: number,
  limit: number,
  status: string | undefined,
  auth: AuthShape,
) => {
  try {
    return await getSuppliersCore(axiosinstance, page, limit, status, auth);
  } catch (e) {
    return Promise.reject(e);
  }
};

export const getSupplierInformation = async (
  axiosinstance: AxiosInstance,
  supplierId: string,
  auth: AuthShape,
) => {
  try {
    return await getSupplierByIdCore(axiosinstance, supplierId, auth);
  } catch (e) {
    return Promise.reject(e);
  }
};

export const addSupplierInformation = async (
  axiosinstance: AxiosInstance,
  payload: any,
  auth: AuthShape,
) => {
  try {
    return await addSupplierCore(axiosinstance, payload, auth);
  } catch (e) {
    return Promise.reject(e);
  }
};

export const updateSupplierInformation = async (
  axiosinstance: AxiosInstance,
  supplierId: string,
  payload: any,
  auth: AuthShape,
) => {
  try {
    return await updateSupplierCore(axiosinstance, supplierId, payload, auth);
  } catch (e) {
    return Promise.reject(e);
  }
};

export const getPurchaseOrdersInformation = async (
  axiosinstance: AxiosInstance,
  options: { supplierId?: string; purchaseOrderId?: string },
  auth: AuthShape,
) => {
  try {
    return await getPurchaseOrdersCore(axiosinstance, options, auth);
  } catch (e) {
    return Promise.reject(e);
  }
};

export const getPurchaseOrderInformation = async (
  axiosinstance: AxiosInstance,
  purchaseOrderId: string,
  auth: AuthShape,
) => {
  try {
    return await getPurchaseOrderByIdCore(axiosinstance, purchaseOrderId, auth);
  } catch (e) {
    return Promise.reject(e);
  }
};

export const addPurchaseOrderInformation = async (
  axiosinstance: AxiosInstance,
  payload: any,
  auth: AuthShape,
) => {
  try {
    return await addPurchaseOrderCore(axiosinstance, payload, auth);
  } catch (e) {
    return Promise.reject(e);
  }
};

export const updatePurchaseOrderInformation = async (
  axiosinstance: AxiosInstance,
  purchaseOrderId: string,
  payload: any,
  auth: AuthShape,
) => {
  try {
    return await updatePurchaseOrderCore(
      axiosinstance,
      purchaseOrderId,
      payload,
      auth,
    );
  } catch (e) {
    return Promise.reject(e);
  }
};

export const sendPurchaseOrderToSupplier = async (
  axiosinstance: AxiosInstance,
  payload: { purchaseOrderId: string; channels: string[] },
  auth: AuthShape,
) => {
  try {
    return await sendPurchaseOrderCore(axiosinstance, payload, auth);
  } catch (e) {
    return Promise.reject(e);
  }
};

export const cancelPurchaseOrderInformation = async (
  axiosinstance: AxiosInstance,
  payload: { purchaseOrderId: string },
  auth: AuthShape,
) => {
  try {
    return await cancelPurchaseOrderCore(axiosinstance, payload, auth);
  } catch (e) {
    return Promise.reject(e);
  }
};

export const getSupplierInvoicesInformation = async (
  axiosinstance: AxiosInstance,
  options: { supplierId?: string; purchaseOrderId?: string },
  auth: AuthShape,
) => {
  try {
    return await getSupplierInvoicesCore(axiosinstance, options, auth);
  } catch (e) {
    return Promise.reject(e);
  }
};

export const addSupplierInvoiceInformation = async (
  axiosinstance: AxiosInstance,
  payload: any,
  auth: AuthShape,
) => {
  try {
    return await addSupplierInvoiceCore(axiosinstance, payload, auth);
  } catch (e) {
    return Promise.reject(e);
  }
};

export const getGoodsReceiptsInformation = async (
  axiosinstance: AxiosInstance,
  purchaseOrderId: string,
  auth: AuthShape,
) => {
  try {
    return await getGoodsReceiptsCore(axiosinstance, purchaseOrderId, auth);
  } catch (e) {
    return Promise.reject(e);
  }
};

export const getPurchaseOrderForReceipt = async (
  axiosinstance: AxiosInstance,
  purchaseOrderId: string,
  auth: AuthShape,
) => {
  try {
    return await getPurchaseOrderForReceiptCore(
      axiosinstance,
      purchaseOrderId,
      auth,
    );
  } catch (e) {
    return Promise.reject(e);
  }
};

export const createGoodsReceipt = async (
  axiosinstance: AxiosInstance,
  payload: any,
  auth: AuthShape,
) => {
  try {
    return await createGoodsReceiptCore(axiosinstance, payload, auth);
  } catch (e) {
    return Promise.reject(e);
  }
};
